/**
 * Auth API for local Identity (POST /auth/login, GET /auth/me, POST /auth/logout).
 * Token is stored in localStorage and sent as Authorization: Bearer.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const AUTH_TOKEN_KEY = 'authToken';

function getToken() {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

function setToken(token) {
  if (token) localStorage.setItem(AUTH_TOKEN_KEY, token);
  else localStorage.removeItem(AUTH_TOKEN_KEY);
}

/**
 * Headers for authenticated API calls (Bearer token when present).
 */
function createAuthHeaders(extra = {}) {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...extra
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

/**
 * POST /api/auth/login — body: { email, password }
 * Returns { user, token? }. On failure throws with .code (e.g. INVALID_CREDENTIALS).
 */
async function login(credentials) {
  const { email, password } = credentials || {};
  const res = await fetch(`${API_BASE}/api/auth/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: (email || '').trim(), password: password || '' })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || 'Login failed');
    err.code = data.code || 'LOGIN_FAILED';
    throw err;
  }
  if (data.token) setToken(data.token);
  return {
    user: {
      userId: data.userId,
      email: data.email,
      displayName: data.displayName,
      roles: data.roles || [],
      isActive: data.isActive,
      lastLoginAt: data.lastLoginAt,
      effectiveEmail: data.effectiveEmail || data.email,
      impersonating: Boolean(data.impersonating),
      impersonatedEmail: data.impersonatedEmail || null,
      impersonatedConfigId: null,
      mustChangePassword: Boolean(data.mustChangePassword)
    },
    token: data.token,
    mustChangePassword: Boolean(data.mustChangePassword)
  };
}

/**
 * GET /api/auth/me — current user. Uses cookie or Authorization: Bearer.
 * On 401 throws with code AUTH_REQUIRED.
 */
async function getCurrentUser() {
  const token = getToken();
  const headers = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}/api/auth/me`, {
    method: 'GET',
    credentials: 'include',
    headers
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) {
    const err = new Error(data.message || 'Authentication required');
    err.code = data.code || 'AUTH_REQUIRED';
    throw err;
  }
  if (!res.ok) throw new Error(data.message || 'Failed to get user');
  return data;
}

/**
 * POST /api/auth/logout — clear session and token.
 */
async function logout() {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  try {
    await fetch(`${API_BASE}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers
    });
  } finally {
    setToken(null);
  }
}

/**
 * Impersonate the SharePoint configuration owner. Pass numeric configId from GET /auth/impersonate/options.
 */
async function impersonate(configId) {
  const id = parseInt(configId, 10);
  const res = await fetch(`${API_BASE}/api/auth/impersonate`, {
    method: 'POST',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify(Number.isNaN(id) || id < 1 ? {} : { configId: id })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || 'Impersonation failed');
    err.code = data.code;
    throw err;
  }
  if (data.token) setToken(data.token);
  return data;
}

async function impersonateClear() {
  const res = await fetch(`${API_BASE}/api/auth/impersonate/clear`, {
    method: 'POST',
    credentials: 'include',
    headers: createAuthHeaders()
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || 'Failed to clear impersonation');
  if (data.token) setToken(data.token);
  return data;
}

async function getImpersonationOptions(q = '') {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  const res = await fetch(`${API_BASE}/api/auth/impersonate/options?${params}`, {
    credentials: 'include',
    headers: createAuthHeaders({ Accept: 'application/json' })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || 'Failed to load configurations');
  return data.configs || data.customers || [];
}

export const authApi = {
  login,
  logout,
  getCurrentUser,
  impersonate,
  impersonateClear,
  getImpersonationOptions,
  getToken,
  setToken,
  createAuthHeaders,
  AUTH_TOKEN_KEY
};
export default authApi;
